/* eslint-disable @typescript-eslint/no-explicit-any */
import { EmailProviderSelection } from "@/components/email-provider-selection";
import { TestProgressAnimation } from "@/components/popups/test-progress-animation";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  useCheckPlacementTestExists,
  useInboxPlacement,
  usePlacementAccounts,
  useWarmups,
} from "@/hooks/use-inboxplacement";
import { useServices } from "@/services/hooks/useServices";
import { usePlacementTestStore } from "@/stores/placementTest.store";
import { EmailProvider, PlacementType } from "@/types/inboxPlacements.types";
import { useRouter } from "@tanstack/react-router";
import { AlertCircle, CheckCircle, Copy, Info, Loader2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  SearchableMultiSelect,
  SelectOption,
} from "@/components/ui/searchable-multi-select";
import { allEmailProvidersDetails, cn } from "@/lib/utils";
import CopyButton from "../copy-button";
import { Textarea } from "../ui/textarea";
import { ScrollArea } from "../ui/scroll-area";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { authProviderIcons } from "@/pages/mailbox/esp-providers";
import { AuthProvider } from "@/types/common";

// Add Provider interface
interface Provider {
  id: string;
  name: string;
  value: EmailProvider;
  icon: string | React.ReactNode;
  type: "b2b" | "b2c";
  description?: string;
}

interface RunPlacementTestProps {
  open: boolean;
  allowedEspData?: {
    organizationId: string;
    allowedEspTypes: string[];
    allEspAllowed: boolean;
  } | null;
  onOpenChange: (open: boolean) => void;
}

export function RunPlacementTest({
  open,
  allowedEspData,
  onOpenChange,
}: RunPlacementTestProps) {
  const {
    setIsRunningPlacementOpen,
    addRunningTestData,
    updateRunningTestData,
    removeRunningTestData,
  } = usePlacementTestStore();

  const [activeTab, setActiveTab] = useState<string>("connected-mailbox");
  const [testStatus, setTestStatus] = useState<
    "idle" | "running" | "completed" | "failed"
  >("idle");
  const router = useRouter();
  const [testEmail, setTestEmail] = useState<string>("");
  const senderEmailRef = useRef<string>("");
  const [testType, setTestType] = useState<string>("B2B"); // Always B2B now
  // Add ESP type selection state
  const [selectedEspType, setSelectedEspType] = useState<AuthProvider>(
    AuthProvider.SMTP
  );

  const [selectedProviders, setSelectedProviders] = useState<string[]>([]);
  const [currentTestId, setCurrentTestId] = useState<string | null>(null);
  const { inboxPlacementService } = useServices();

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      // Reset state after dialog closes with a delay
      const timeout = setTimeout(() => {
        setTestStatus("idle");
        setCurrentTestId(null);
      }, 500);

      return () => clearTimeout(timeout);
    }
  }, [open]);

  // main handle run test function
  const handleRunTest = async (
    senderEmail: string,
    testType: string,
    testEmails?: string[],
    provider?: string[],
    senderProfileTestId?: string
  ) => {
    setTestStatus("running");
    senderEmailRef.current = senderEmail;

    setSelectedProviders(provider as string[]);
    try {
      const isConnectedMailbox = activeTab === "connected-mailbox";
      let testId: string | undefined;

      if (isConnectedMailbox) {
        const result = await inboxPlacementService.runPlacementTest(
          senderEmail,
          testType,
          provider as string[],
          selectedEspType
        );

        window.cioanalytics?.track("inbox_placement_test_run_mailbox", {
          senderEmail,
          testType,
          provider: provider as string[],
        });

        testId = Array.isArray(result)
          ? result[0]?.testId
          : (result as { testId?: string })?.testId;
      } else {
        // handle sender profile
        testId = senderProfileTestId;

        window.cioanalytics?.track("inbox_placement_test_run_sender_profile", {
          senderEmail,
          testType,
          provider: provider as string[],
        });
      }

      if (testId) {
        setCurrentTestId(testId);
        addRunningTestData({
          senderEmail,
          testType: PlacementType.B2B, // Always B2B now
          provider: provider as string[],
          testId,
          status: "running",
        });
        setIsRunningPlacementOpen(false);
      } else {
        setTestStatus("completed");
        toast.success("Placement test created successfully");
        onOpenChange(false);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to create placement test");
      setTestStatus("completed");
    }
  };

  const handleTestComplete = useCallback(
    (testData: any) => {
      if (testData.status === "completed") {
        onOpenChange(false);
        setTestStatus("completed");
        toast.success("Placement test completed successfully");
        if (currentTestId) {
          updateRunningTestData(currentTestId, { status: "completed" });
        }
        // Optional: Close dialog and redirect to results after a delay
        setTimeout(() => {
          router.navigate({
            to: `/placement-test/${senderEmailRef.current}`,
            search: {
              type: PlacementType.B2B, // Always B2B now
              _testId: currentTestId || undefined,
            },
          });

          removeRunningTestData(currentTestId as string);
        }, 2000);
      } else if (testData.status === "failed") {
        setTestStatus("failed");
        toast.error(testData.message || "Test failed");
        // Close the dialog when test fails
        onOpenChange(false);
        if (currentTestId) {
          updateRunningTestData(currentTestId, { status: "completed" });
        }
      }
    },
    [
      currentTestId,
      onOpenChange,
      router,
      testType,
      updateRunningTestData,
      removeRunningTestData,
    ]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(!currentTestId ? " md:max-w-2xl" : "md:max-w-5xl  p-0")}
      >
        {testStatus !== "running" && !currentTestId ? (
          <DialogHeader>
            <DialogTitle>Run Inbox Placement Test</DialogTitle>
          </DialogHeader>
        ) : (
          <></>
        )}

        {testStatus === "running" && currentTestId ? (
          <TestProgressAnimation
            testId={currentTestId}
            senderEmail={senderEmailRef.current}
            testType={testType}
            provider={selectedProviders}
            onComplete={handleTestComplete}
          />
        ) : (
          <Tabs
            defaultValue="connected-mailbox"
            value={activeTab}
            onValueChange={setActiveTab}
            className="space-y-4"
          >
            <div className="flex justify-between items-center">
              <TabsList>
                <TabsTrigger value="connected-mailbox">
                  Connected Mailbox
                </TabsTrigger>
                <TabsTrigger value="sender-profile">Sender Profile</TabsTrigger>
              </TabsList>

              {/* {selectedProviders.length > 0 && (
                <div className="text-sm text-muted-foreground">
                  {selectedProviders.length} of {localB2bProviders.length + localB2cProviders.length} ESPs selected
                  <span className="ml-2">({selectedProviders.length * 5} emails will be sent)</span>
                </div>
              )} */}
            </div>

            <TabsContent value="connected-mailbox" className="space-y-4">
              <Card>
                {/* <CardHeader>
                  <CardTitle>Test Configuration</CardTitle>
                  <CardDescription>
                    Configure your test using a connected mailbox
                  </CardDescription>
                </CardHeader> */}
                <CardContent className="p-3 pt-2">
                  <ConnectedMailboxForm
                    allowedEspData={allowedEspData}
                    onRunTest={handleRunTest}
                    setTestEmail={setTestEmail}
                    setTestType={setTestType}
                    selectedEspType={selectedEspType}
                    setSelectedEspType={setSelectedEspType}
                    isRunning={testStatus === "running"}
                    setCurrentTestId={setCurrentTestId}
                  />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="sender-profile" className="space-y-4">
              <Card>
                {/* <CardHeader>
                  <CardTitle>Test Configuration</CardTitle>
                  <CardDescription>
                    Configure your test using a sender profile
                  </CardDescription>
                </CardHeader> */}
                <CardContent className="p-3 pt-2">
                  <SenderProfileForm
                    onRunTest={handleRunTest}
                    setTestEmail={(value: string) => setTestEmail(value)}
                    setTestType={(value: string) => setTestType(value)}
                    isRunning={testStatus === "running"}
                    setCurrentTestId={setCurrentTestId}
                    allowedEspData={allowedEspData}
                  />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface ConnectedMailboxFormProps {
  onRunTest: (
    senderEmail: string,
    testType: string,
    testEmails?: string[],
    provider?: string[]
  ) => void;
  isRunning: boolean;
  setTestEmail: (testEmail: string) => void;
  setTestType: (testType: string) => void;
  setCurrentTestId: (testId: string | null) => void;
  selectedEspType: AuthProvider;
  setSelectedEspType: (espType: AuthProvider) => void;
  allowedEspData?: {
    organizationId: string;
    allowedEspTypes: string[];
    allEspAllowed: boolean;
  } | null;
}

function ConnectedMailboxForm({
  onRunTest,
  isRunning,
  setTestEmail,
  setTestType,
  selectedEspType,
  setSelectedEspType,
  setCurrentTestId,
  allowedEspData,
}: ConnectedMailboxFormProps) {
  const [recipientType, setRecipientType] = useState<"b2b" | "b2c">("b2b"); // Keep for provider selection, but API will use B2B
  const testTypeRecipientRef = useRef<"b2b" | "b2c">("b2b");
  const [selectedProviders, setSelectedProviders] = useState<Provider[]>([]);

  // Search state
  const [mailboxSearchTerm, setMailboxSearchTerm] = useState("");
  const [templateSearchTerm, setTemplateSearchTerm] = useState("");
  const [debouncedMailboxSearchTerm, setDebouncedMailboxSearchTerm] =
    useState("");
  const [debouncedTemplateSearchTerm, setDebouncedTemplateSearchTerm] =
    useState("");
  const [isMailboxSearching, setIsMailboxSearching] = useState(false);

  const { mailbox, template, mailboxLoading, templateLoading } =
    useInboxPlacement(debouncedMailboxSearchTerm, debouncedTemplateSearchTerm, selectedEspType);
  const [selectedTestEmails, setSelectedTestEmails] = useState<string>(
    mailbox?.[0]?.email || ""
  );
  const [selectedTemplate, setSelectedTemplate] = useState<string>(
    template?.[0]?._id || ""
  );
  const { inboxPlacementService } = useServices();

  const { data: testExists, isLoading: testExistsLoading } =
    useCheckPlacementTestExists(selectedTestEmails);

  // Reset selected mailbox when ESP type changes
  useEffect(() => {
    setSelectedTestEmails("");
    setMailboxSearchTerm("");
  }, [selectedEspType]);

  // Debounce mailbox search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedMailboxSearchTerm(mailboxSearchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [mailboxSearchTerm]);

  // Debounce template search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTemplateSearchTerm(templateSearchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [templateSearchTerm]);

  const handleMailboxSearchChange = (term: string) => {
    setMailboxSearchTerm(term);
    setIsMailboxSearching(!!term);
  };

  // Convert mailbox data to format expected by SearchableMultiSelect
  const mailboxOptions: SelectOption[] =
    mailbox?.map((item: any) => ({
      value: item.email,
      label: item.email,
      icon: authProviderIcons[item.authProvider as AuthProvider]?.icon,
      tooltip: authProviderIcons[item.authProvider as AuthProvider]?.tooltip,
    })) || [];

  // Convert template data to format expected by SearchableMultiSelect
  const templateOptions: SelectOption[] =
    template?.map((item: any) => ({
      value: item._id,
      label: item.title,
    })) || [];

  // Get test mailboxes based on selected providers
  const getTestEmails = () => {
    if (selectedProviders.length === 0) {
      return getDefaultTestEmails();
    }

    const testEmails: string[] = [];

    // Map provider IDs to test email addresses
    selectedProviders.forEach((provider) => {
      switch (provider.id) {
        case "gmail":
          testEmails.push("test-inbox1@gmail.com");
          break;
        case "outlook":
          testEmails.push("test-inbox2@outlook.com");
          break;
        case "yahoo":
          testEmails.push("test-inbox3@yahoo.com");
          break;
        // Add other cases as needed
        default:
          break;
      }
    });

    return testEmails;
  };

  // Default test emails - API will handle based on selected providers
  const getDefaultTestEmails = () => {
    // Return empty array, API will determine test emails based on selected providers
    return [];
  };

  //connected mailbox handle run test function
  const handleRunTest = async () => {
    const testEmails = getTestEmails();

    // Notify parent component to show running state - always use "b2b" for API
    onRunTest(
      selectedTestEmails,
      "b2b", // Always use B2B API
      testEmails,
      selectedProviders.map((p) => p.value)
    );

    try {
      // Send test email with the selected configuration - always use "b2b" for API
      const result = await inboxPlacementService.sendTestEmail(
        selectedTestEmails,
        selectedTemplate,
        "b2b", // Always use B2B API
        selectedEspType
      );

      // Check if result is an array or object and extract testId accordingly
      if (Array.isArray(result) && result.length > 0 && result[0]?.testId) {
        setCurrentTestId(result[0].testId as string);
      } else if (result && typeof result === "object" && "testId" in result) {
        setCurrentTestId((result as { testId: string }).testId);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to send test email");
    }
  };

  return (
    <div className="space-y-3 max-h-[70vh] overflow-y-auto">
      {/* ESP Type Selection - Must be selected first */}
      <div className="space-y-2">
        <Label htmlFor="esp-type" className="text-sm font-medium">
          Email Service Provider Type
        </Label>
        <Select
          value={selectedEspType}
          onValueChange={(value) => setSelectedEspType(value as AuthProvider)}
        >
          <SelectTrigger id="esp-type">
            <SelectValue placeholder="Select ESP type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={AuthProvider.SMTP}>
              SMTP - Standard SMTP Server
            </SelectItem>
            <SelectItem value={AuthProvider.GOOGLE}>
              Google - Gmail/Google Workspace
            </SelectItem>
            <SelectItem value={AuthProvider.MICROSOFT}>
              Microsoft - Outlook/Office 365
            </SelectItem>
            <SelectItem value={AuthProvider.AWS_API}>Amazon SES</SelectItem>
            <SelectItem value={AuthProvider.SENDGRID_API}>SendGrid</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          Select the email service provider type to see available mailboxes
        </p>
      </div>

      {/* Mailbox Selection - Only show after ESP type is selected */}
      {selectedEspType && (
        <div className="space-y-2">
          <SearchableMultiSelect
            label="Connected Mailbox"
            options={mailboxOptions}
            selectedValues={selectedTestEmails ? [selectedTestEmails] : []}
            onChange={(values) => {
              const value = values[0] || "";
              setSelectedTestEmails(value);
              setTestEmail(value);
            }}
            searchTerm={mailboxSearchTerm}
            setSearchTerm={handleMailboxSearchChange}
            placeholder="Select a mailbox"
            maxHeight={200}
            loading={mailboxLoading}
            singleSelect={true}
          />
          {testExistsLoading && (
            <div className="text-sm text-gray-500 flex items-center">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              <span>Checking if test already exists...</span>
            </div>
          )}
          {testExists?.success && (
            <div className="text-sm text-red-500">
              Test already exists for this mailbox, Please try again later or
              use a different email address.
            </div>
          )}
        </div>
      )}

      {/* Show message when no ESP type is selected */}
      {!selectedEspType && (
        <div className="p-4 text-center text-muted-foreground border rounded-md">
          Please select an Email Service Provider type above to see available
          mailboxes
        </div>
      )}

      {/* Email Template - Only show after mailbox is selected */}
      {selectedEspType && selectedTestEmails && (
        <div className="space-y-2">
          <SearchableMultiSelect
            label="Email Template"
            options={templateOptions}
            selectedValues={selectedTemplate ? [selectedTemplate] : []}
            onChange={(values) => {
              setSelectedTemplate(values[0] || "");
            }}
            searchTerm={templateSearchTerm}
            setSearchTerm={setTemplateSearchTerm}
            placeholder="Select a template"
            maxHeight={200}
            loading={templateLoading}
            singleSelect={true}
          />
        </div>
      )}

      {/* Email Service Providers - Only show after template is selected */}
      {selectedEspType && selectedTestEmails && selectedTemplate && (
        <div className="space-y-1">
          <Label htmlFor="providers">Email Service Providers</Label>
          <div className="border rounded-md p-4">
            <EmailProviderSelection
              testTypeRecipientRef={testTypeRecipientRef.current}
              onProvidersChange={setSelectedProviders}
              allowedEspData={allowedEspData}
              onProviderTypeChange={(type) => {
                // Keep for UI state, but API will always use B2B
                setRecipientType(type);
                setTestType("b2b"); // Always B2B for API
                testTypeRecipientRef.current = type;
              }}
            />
          </div>
        </div>
      )}

      {/* Run Test Button - Only show when all required fields are filled */}
      {selectedEspType && selectedTestEmails && selectedTemplate && (
        <Button
          onClick={() => handleRunTest()}
          disabled={
            isRunning ||
            selectedProviders.length === 0 ||
            testExists?.success ||
            testExistsLoading
          }
          className="w-full mt-6"
        >
          {isRunning ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Running Test...
            </>
          ) : (
            "Run Inbox Placement Test"
          )}
        </Button>
      )}
    </div>
  );
}

interface SenderProfileFormProps {
  onRunTest: (
    senderEmail: string,
    testType: string,
    testEmails?: string[],
    provider?: string[],
    senderProfileTestId?: string
  ) => void;
  isRunning: boolean;
  setTestEmail: (testEmail: string) => void;
  setTestType: (testType: string) => void;
  setCurrentTestId: (testId: string | null) => void;
  allowedEspData?: {
    organizationId: string;
    allowedEspTypes: string[];
    allEspAllowed: boolean;
  } | null;
}

const GROUP_SIZE = 20;

type EmailSeparator =
  | "comma"
  | "semicolon"
  | "newline"
  | "comma-newline"
  | "semicolon-newline";

const getFormattedEmails = (
  emails: string[],
  emailSeparator: EmailSeparator
) => {
  if (emailSeparator === "comma") {
    return emails.join(", ");
  } else if (emailSeparator === "semicolon") {
    return emails.join("; ");
  } else if (emailSeparator === "newline") {
    return emails.join("\n");
  } else if (emailSeparator === "comma-newline") {
    return emails.join(", \n");
  } else if (emailSeparator === "semicolon-newline") {
    return emails.join("; \n");
  }
  return emails.join(", ");
};

function SenderProfileForm({
  onRunTest,
  isRunning,
  setTestEmail,
  setTestType,
  setCurrentTestId,
  allowedEspData,
}: SenderProfileFormProps) {
  const [recipientType, setRecipientType] = useState<string>("b2b"); // Keep for provider selection, but API will use B2B
  const [selectedProviders, setSelectedProviders] = useState<Provider[]>([]);
  const [filteredSeedEmails, setFilteredSeedEmails] = useState<
    { _id: string; email: string; provider: EmailProvider }[]
  >([]);

  const testTypeRecipientRef = useRef<"b2b" | "b2c">("b2b");
  const senderProfileTestId = useRef<string | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [senderEmail, setSenderEmail] = useState<string>("");
  const [currentStep, setCurrentStep] = useState<"set-up" | "copy-emails">(
    "set-up"
  );
  const [copiedGroups, setCopiedGroups] = useState<boolean[]>([]);

  const [emailSeparator, setEmailSeparator] = useState<EmailSeparator>("comma");
  const { inboxPlacementService } = useServices();

  const { data: warmups, isLoading: warmupsLoading } = useWarmups();

  // Always use "b2b" for placement accounts API call
  const { data: placementAccounts, isLoading: placementAccountsLoading } =
    usePlacementAccounts("b2b");

  const { data: testExists, isFetching: testExistsLoading } =
    useCheckPlacementTestExists(senderEmail);

  useEffect(() => {
    setSenderEmail(warmups?.[0]?.email || "");
    setTestType("b2b"); // Always B2B
  }, [warmups]);

  // group seed accounts in 20 accounts each if more than 20
  const emailGroups = useMemo(() => {
    if (!filteredSeedEmails?.length) return [];

    const emails = filteredSeedEmails.map((account) => account.email);
    const groups = [];

    for (let i = 0; i < emails.length; i += GROUP_SIZE) {
      const groupEmails = emails.slice(i, i + GROUP_SIZE);
      groups.push({
        emails: groupEmails,
        count: groupEmails.length,
      });
    }

    return groups;
  }, [filteredSeedEmails]);

  useEffect(() => {
    setCopiedGroups(Array(emailGroups.length).fill(false));
  }, [emailGroups.length]);

  const firstStepSubmit = async () => {
    setIsLoading(true);
    try {
      if (!placementAccounts) {
        throw new Error("No placement accounts found");
      }

      const providerValues = selectedProviders.map((p) => p.value);

      if (providerValues.length === 0) {
        throw new Error("No providers selected");
      }

      const filteredEmails = placementAccounts.filter((email) =>
        providerValues.includes(email.provider)
      );

      if (filteredEmails.length === 0) {
        throw new Error("No test emails found");
      }

      setFilteredSeedEmails(filteredEmails);

      // Always use "b2b" for API call
      const result = await inboxPlacementService.runPlacementTestsender(
        senderEmail,
        "b2b", // Always use B2B API
        filteredEmails.map((email) => email.email),
        providerValues
      );

      const testId = Array.isArray(result)
        ? result[0]?.testId
        : (result as { testId?: string })?.testId;

      if (testId) {
        senderProfileTestId.current = testId;
      }

      setCurrentStep("copy-emails");
    } catch (error: any) {
      console.error(error);
      toast.error(
        error instanceof Error ? error.message : "Failed to run placement test"
      );
    } finally {
      setIsLoading(false);
    }
  };

  // handle sender profile run test function
  const handleRunTest = useCallback(async () => {
    try {
      // Ensure testEmails is always an array
      const emailsToTest = filteredSeedEmails.map((email) => email.email);

      const providerValues = selectedProviders.map((p) => p.value);
      // First notify parent component to show running state

      // Always use "b2b" for API call
      onRunTest(
        senderEmail,
        "b2b", // Always use B2B API
        emailsToTest,
        providerValues,
        senderProfileTestId.current || undefined
      );

      // Run the placement test for the sender
      setTestEmail(senderEmail);
      setTestType("b2b"); // Always B2B
    } catch (error) {
      console.error(error);
      toast.error("Failed to run placement test");
    }
  }, [
    filteredSeedEmails,
    onRunTest,
    recipientType,
    selectedProviders,
    senderEmail,
    setTestEmail,
    setTestType,
  ]);

  // email count summary based on selected providers
  const seedAccountSummary: Record<EmailProvider, number> = useMemo(() => {
    if (!filteredSeedEmails?.length) return {} as Record<EmailProvider, number>;

    return filteredSeedEmails.reduce(
      (acc, email) => {
        acc[email.provider] = (acc[email.provider] || 0) + 1;
        return acc;
      },
      {} as Record<EmailProvider, number>
    );
  }, [filteredSeedEmails]);

  const isAllEmailsCopied = useMemo(() => {
    if (currentStep === "copy-emails") {
      return copiedGroups.length > 0 && copiedGroups.every(Boolean);
    }
    return false;
  }, [copiedGroups, currentStep]);

  const isExceededGroupSize = filteredSeedEmails.length > GROUP_SIZE;

  if (warmupsLoading || placementAccountsLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto">
      {currentStep === "set-up" ? (
        <>
          <div className="flex justify-between items-center">
            <h3 className="text-base font-medium">Test Setup</h3>
            <div className="text-sm text-muted-foreground">Step 1 of 2</div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="profile">Seed Account</Label>
            <Select
              value={senderEmail}
              onValueChange={(value) => {
                setSenderEmail(value);
                setTestEmail(value);
              }}
              disabled={placementAccountsLoading}
            >
              <SelectTrigger id="profile">
                <SelectValue placeholder="Select a sender profile" />
              </SelectTrigger>
              <SelectContent className="max-h-[30vh] overflow-y-auto">
                {warmups?.length && warmups?.length > 0 ? (
                  warmups?.map((warmup: any) => (
                    <SelectItem key={warmup.email} value={warmup.email}>
                      {warmup.email}
                    </SelectItem>
                  ))
                ) : (
                  <div className="text-sm text-muted-foreground flex flex-col items-center justify-center px-2 py-4">
                    <span>No warmups found</span>
                    <span>Please add a sender profile to your account.</span>
                  </div>
                )}
              </SelectContent>
            </Select>
            {testExistsLoading && (
              <div className="text-sm text-gray-500 flex items-center">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                <span>Checking if test already exists...</span>
              </div>
            )}
            {testExists?.success && (
              <div className="text-sm text-red-500">
                Test already exists for this mailbox, Please try again later or
                use a different email address.
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="providers">Email Service Providers</Label>
            <div
              className={`border rounded-md p-4 ${
                placementAccountsLoading ? "opacity-50" : ""
              } relative`}
            >
              {placementAccountsLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-background/50 z-10 rounded-md">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              )}
              <EmailProviderSelection
                allowedEspData={allowedEspData}
                onProvidersChange={setSelectedProviders}
                testTypeRecipientRef={testTypeRecipientRef.current}
                onProviderTypeChange={(type) => {
                  // Keep for UI state, but API will always use B2B
                  setRecipientType(type);
                  setTestType("b2b"); // Always B2B for API
                  testTypeRecipientRef.current = type;
                }}
                disabled={placementAccountsLoading}
                initialProviders={selectedProviders}
              />
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <h3 className="text-base font-medium">Test Seed Emails </h3>
              <div className="text-sm text-muted-foreground">Step 2 of 2</div>
            </div>
            <div
              className={`bg-background rounded-md border p-3 relative ${
                placementAccountsLoading ? "opacity-50" : ""
              }`}
            >
              {placementAccountsLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-background/80 z-10">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              )}

              {isExceededGroupSize && <GroupingAlterMessage className="mb-2" />}

              {/* count summary */}
              {selectedProviders.length > 0 && (
                <div className="mb-2 pb-2 border-b border-dashed">
                  {selectedProviders.map((provider) => (
                    <div
                      key={provider.value}
                      className="text-xs inline-flex items-center rounded-full bg-primary/10 text-primary px-2 py-0.5 font-medium mr-2 mb-2"
                    >
                      {provider.name}:{" "}
                      {seedAccountSummary?.[provider.value] || 0}
                    </div>
                  ))}
                </div>
              )}

              <div className="flex items-center justify-end gap-2">
                <Label htmlFor="separator-select" className="whitespace-nowrap">
                  Separated by
                </Label>
                <Select
                  value={emailSeparator}
                  onValueChange={(value: EmailSeparator) =>
                    setEmailSeparator(value)
                  }
                >
                  <SelectTrigger id="separator-select" className="w-fit">
                    <SelectValue placeholder="Select separator" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="comma">Comma</SelectItem>
                    <SelectItem value="semicolon">Semicolon</SelectItem>
                    <SelectItem value="newline">Newline</SelectItem>
                    <SelectItem value="comma-newline">
                      Comma + Newline
                    </SelectItem>
                    <SelectItem value="semicolon-newline">
                      Semicolon + Newline
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* email list */}
              {isExceededGroupSize ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                  {emailGroups.map((group, index) => (
                    <EmailGroup
                      key={index}
                      emails={group.emails}
                      title={`Group ${index + 1} of ${emailGroups.length} (${
                        group.count
                      } emails)`}
                      emailSeparator={emailSeparator}
                      isCopied={copiedGroups[index] || false}
                      onCopy={() => {
                        setCopiedGroups((prev) => {
                          const updated = [...prev];
                          updated[index] = true;
                          return updated;
                        });
                      }}
                    />
                  ))}
                </div>
              ) : (
                <EmailGroup
                  emails={filteredSeedEmails?.map((email) => email.email)}
                  title={`Total ${filteredSeedEmails?.length} emails`}
                  emailSeparator={emailSeparator}
                  isCopied={copiedGroups[0] || false}
                  onCopy={() => {
                    setCopiedGroups([true]);
                  }}
                />
              )}
            </div>
          </div>
        </>
      )}

      {currentStep === "copy-emails" && copiedGroups.length > 0 && (
        <div className="flex items-center gap-2">
          {isAllEmailsCopied ? (
            <CheckCircle className="size-4 text-green-500 shrink-0" />
          ) : (
            <AlertCircle className="size-4 text-red-500 shrink-0" />
          )}
          <span className="text-sm text-start text-muted-foreground">
            To enable the submit button, copy and send
            {isExceededGroupSize ? " emails to each group." : " email."}
          </span>
        </div>
      )}

      {currentStep === "set-up" && (
        <Button
          onClick={() => firstStepSubmit()}
          disabled={
            isRunning ||
            isLoading ||
            placementAccountsLoading ||
            testExists?.success ||
            testExistsLoading
          }
          className="w-full"
        >
          {isRunning ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Running Test...
            </>
          ) : placementAccountsLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Loading Test Accounts...
            </>
          ) : isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Submitting Test...
            </>
          ) : (
            "Next: Copy Emails"
          )}
        </Button>
      )}
      {currentStep === "copy-emails" && (
        <Button
          onClick={() => handleRunTest()}
          disabled={
            isRunning ||
            !filteredSeedEmails?.length ||
            placementAccountsLoading ||
            !isAllEmailsCopied
          }
          className="w-full"
        >
          {isRunning ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Running Test...
            </>
          ) : (
            "Run Inbox Placement Test"
          )}
        </Button>
      )}
    </div>
  );
}

const EmailGroup = ({
  emails,
  title,
  emailSeparator,
  onCopy,
  isCopied,
}: {
  emails: string[];
  title: string;
  emailSeparator: EmailSeparator;
  onCopy: () => void;
  isCopied: boolean;
}) => {
  return (
    <div className="">
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm text-muted-foreground">{title}</span>
        <div className="flex items-center gap-2">
          {isCopied && <span className="text-sm text-green-500">Copied!</span>}
          <CustomCopyButton
            textCallback={() => getFormattedEmails(emails, emailSeparator)}
            onCopy={onCopy}
          />
        </div>
      </div>

      <EmailListRenderer
        emails={emails}
        separator={emailSeparator}
        onCopy={onCopy}
        className={isCopied ? "border border-green-500 bg-green-50" : ""}
      />
    </div>
  );
};

const EmailListRenderer = ({
  emails,
  separator,
  onCopy,
  className,
}: {
  emails: string[];
  separator: EmailSeparator;
  onCopy?: () => void;
  className?: string;
}) => {
  return (
    <ScrollArea
      className={cn("h-[100px] text-sm bg-secondary rounded-md p-2", className)}
    >
      <div className="" onCopy={() => onCopy?.()}>
        {["comma", "semicolon"].includes(separator) ? (
          getFormattedEmails(emails, separator)
        ) : (
          <>
            {emails.map((email) => (
              <p key={email}>
                {email}
                {separator === "newline"
                  ? "\n"
                  : separator === "comma-newline"
                    ? ", "
                    : ";"}
              </p>
            ))}
          </>
        )}
      </div>
    </ScrollArea>
  );
};

const GroupingAlterMessage = ({ className }: { className?: string }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <Alert variant="info" className={className}>
      <Info className="h-4 w-4" />
      <AlertTitle>Seed Account Grouping</AlertTitle>
      <AlertDescription>
        When the &quot;To&quot; field contains too many addresses (over 20), it
        can significantly increase the likelihood of emails being flagged as
        spam or blocked by email providers.
        {isOpen && (
          <span>
            {" "}
            This happens because large recipient lists may trigger anti-spam
            filters or exceed provider thresholds, compromising deliverability.
            Additionally, testing performance can suffer, leading to less
            reliable results, while processing times may extend due to the
            higher volume To address this, we divide your list into smaller
            groups (up-to 20 seed account per group), ensuring better
            deliverability accuracy, optimized testing performance, and
            efficient processing. This approach aligns with best practices for
            simulating real-world email campaigns.
          </span>
        )}
        <button
          className="text-sm underline text-blue-700 px-2"
          onClick={() => setIsOpen(!isOpen)}
        >
          {isOpen ? "Read Less" : "Read More"}
        </button>
      </AlertDescription>
    </Alert>
  );
};

const CustomCopyButton = ({
  onCopy,
  textCallback,
}: {
  onCopy?: () => void;
  textCallback: () => string;
}) => {
  const copyToClipboard = () => {
    const textToCopy = textCallback();
    navigator.clipboard.writeText(textToCopy);

    onCopy?.();
  };

  return (
    <div className="flex items-center gap-2">
      <Button variant={"ghost"} onClick={() => copyToClipboard()}>
        <Copy className="w-4 h-4" />
      </Button>
    </div>
  );
};
